import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Field, Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import { setMyCarsData } from '../../../../ducks/actions';
import type { RootState } from '../../../../store/configureStore';
import { autopayDetailsFormValidationSchema } from './helpers/validationHelper';
import {
  AutopayDetailsWrapper,
  AutopayDetailsInput,
  AutopayScheduleHeader,
  AutopayNextButton,
  AutopayDetailsCheckbox,
  AutopayConsentWrapper,
  AutopayDueDateWrapper,
  AutopayDetailsRadio,
  AutopayAmountDueWrapper,
  AutopayDefaultAmountDueWrapper,
  AutopayDefaultAmount,
} from './AutopayDetails.styles';
import ElectronicPaymentTerms from '../../../future-packages/electronic-payment-terms-and-conditions';
import { DateMasks, formatDate } from '../../../../utils/dateUtils';
import { formatCurrency } from '../../../../utils/CurrencyUtils';
import { useDualEventTracking } from '../../../../utils/useDualEventTracking';

const AutopayDetails = (props: { loanDetailCar: any }) => {
  const { loanDetailCar } = props;
  const {
    loanDetails,
    vehicle: { vehicleId },
    bankInfo,
  } = loanDetailCar;
  const last4OfPaymentMethod = bankInfo[0]?.last4OfPaymentMethod
    ? bankInfo[0]?.last4OfPaymentMethod
    : '';
  const { paymentMethodType } = bankInfo[0];
  const { regularPaymentAmount, paymentEligibility } = loanDetails;
  const {
    recurringPaymentEligibility: { eligibleRecurringWithdrawals },
  } = paymentEligibility;

  const dueDates = eligibleRecurringWithdrawals.map((wd: any) => {
    return wd.withdrawalStartDate;
  });
  const myCars = useSelector((state: RootState) => state.myCars.cars);
  const payment = formatCurrency(regularPaymentAmount);
  const dueDate = formatDate(dueDates[0], DateMasks.MonthDayYearForwardSlash);
  const dueDateMinusOneDay =
    dueDates.length > 1
      ? formatDate(dueDates[1], DateMasks.MonthDayYearForwardSlash)
      : null;
  const dueDateMinusTwoDay =
    dueDates.length > 2
      ? formatDate(dueDates[2], DateMasks.MonthDayYearForwardSlash)
      : null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(dueDate);
  const history = useHistory();
  const dispatch = useDispatch();
  const { trackEvent } = useDualEventTracking();

  const scheduleSelected = (date: any, value: any) => {
    setScheduledDate(date);
    trackEvent('auto_pay_choose_payment_schedule_button_tap', {
      dueDate: value,
    });
  };

  const consentClicked = () => {
    setIsChecked(!isChecked);
    trackEvent('AutoPay Screen - checkbox selected - Tap');
  };

  const toggleTermsModal = () => {
    if (!showTermsModal) {
      trackEvent('TermsAndConditions Screen');
      trackEvent('auto_pay_terms_link_tap');
    } else {
      trackEvent('auto_pay_tos_dialogue_exit_tap');
    }
    setShowTermsModal(!showTermsModal);
  };

  const updateCarWithAutopayDetails = () => {
    const updateMyCars = (autopayCar: any) => {
      const updatedCars: any = myCars.map((carItem: any) => {
        if (carItem.vehicle.vehicleId === Number(vehicleId.id)) {
          return autopayCar;
        }
        return carItem;
      });
      return updatedCars;
    };
    dispatch(setMyCarsData(updateMyCars(loanDetailCar)));
  };

  const MinimumPaymentAmount = () => {
    return <AutopayDefaultAmount>{payment}</AutopayDefaultAmount>;
  };

  return (
    <AutopayDetailsWrapper>
      <Formik
        initialValues={{
          paymentAmountDueType: 'minimum',
          paymentMinimumAmountDue: regularPaymentAmount,
          paymentOtherAmountDue: '',
          paymentDueDate: '0',
          paymentConsent: false,
        }}
        validationSchema={autopayDetailsFormValidationSchema}
        onSubmit={(values) => {
          setIsSubmitting(true);
          trackEvent('auto_pay_next_button_tap');

          // update loan detail car with auto pay schedule details

          const withdrawAmount =
            values.paymentAmountDueType === 'other'
              ? values.paymentOtherAmountDue
              : payment.substring(1);

          const autopayDetailsRequest = {
            last4AccountNumber: last4OfPaymentMethod,
            paymentOffsetType: values.paymentDueDate,
            withdrawAmount,
            paymentMethodType,
          };

          const autopayDetailsPromptData = {
            amountDueDate: scheduledDate,
            withdrawAmount,
          };

          loanDetailCar.autopayDetailsRequest = autopayDetailsRequest;
          loanDetailCar.autopayDetailsPromptData = autopayDetailsPromptData;

          updateCarWithAutopayDetails();
          history.push(
            `/loan-detail/set-up-autopay/autopay-terms/${vehicleId}`
          );
        }}
        render={({
          touched,
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          errors,
          setFieldValue,
          setErrors,
          setTouched,
        }) => {
          const isSubmitButtonDisabled =
            (Number(values.paymentOtherAmountDue) <= 0 &&
              values.paymentAmountDueType === 'other') ||
            (values.paymentAmountDueType &&
              values.paymentDueDate &&
              values.paymentConsent === false &&
              isSubmitting === false);

          return (
            <form onSubmit={handleSubmit}>
              {/* do not display error message for radio button, user selection 
                  is defaulted, vetted by NEXT button disabled logic */}

              <AutopayDefaultAmountDueWrapper>
                <Field
                  component={AutopayDetailsRadio}
                  name="paymentAmountDueType"
                  id="minAmountDue"
                  label="Total Amount Due"
                  touched={values.paymentAmountDueType}
                  error={errors.paymentAmountDueType}
                  value="minimum"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onClick={() => {
                    setFieldValue('paymentOtherAmountDue', '');
                    setErrors({ paymentOtherAmountDue: undefined });
                    setTouched({ paymentOtherAmountDue: undefined });
                  }}
                />

                {/* this is static text, no user interaction */}
                <MinimumPaymentAmount
                  id="paymentMinimumAmount"
                  name="paymentMinimumAmountDue"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.paymentMinimumAmountDue}
                  touched={touched.paymentMinimumAmountDue}
                  error={errors.paymentMinimumAmountDue}
                />
              </AutopayDefaultAmountDueWrapper>

              <AutopayAmountDueWrapper>
                <Field
                  component={AutopayDetailsRadio}
                  name="paymentAmountDueType"
                  id="otherAmountDue"
                  dataTestId="otherAmountDue"
                  label=""
                  touched={values.paymentAmountDueType}
                  error={errors.paymentAmountDueType}
                  value="other"
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <AutopayDetailsInput
                  id="paymentOtherAmount"
                  inputMode="decimal"
                  disabled={values.paymentAmountDueType === 'minimum'}
                  mode="light"
                  label="OTHER AMOUNT ($0.00)"
                  name="paymentOtherAmountDue"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onSelect={handleChange}
                  value={values.paymentOtherAmountDue}
                  touched={touched.paymentOtherAmountDue}
                  error={errors.paymentOtherAmountDue}
                />
              </AutopayAmountDueWrapper>

              {/* do not display error message, user selection 
                  is defaulted, vetted by NEXT button disabled logic */}

              <AutopayDueDateWrapper>
                <AutopayScheduleHeader>
                  Choose your autopay schedule:
                </AutopayScheduleHeader>

                <Field
                  component={AutopayDetailsRadio}
                  name="paymentDueDate"
                  id="dueDate"
                  label={`Pay on due date - ${dueDate}`}
                  touched={values.paymentDueDate}
                  error={errors.paymentDueDate}
                  value="0"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onClick={() => {
                    scheduleSelected(dueDate, '0');
                  }}
                />
                {dueDateMinusOneDay && (
                  <Field
                    component={AutopayDetailsRadio}
                    name="paymentDueDate"
                    id="dueDateMinusOne"
                    label={`Pay one day early - ${dueDateMinusOneDay}`}
                    touched={values.paymentDueDate}
                    error={errors.paymentDueDate}
                    value="-1"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onClick={() => {
                      scheduleSelected(dueDateMinusOneDay, '-1');
                    }}
                  />
                )}
                {dueDateMinusTwoDay && (
                  <Field
                    component={AutopayDetailsRadio}
                    name="paymentDueDate"
                    id="dueDateMinusTwo"
                    label={`Pay two days early - ${dueDateMinusTwoDay}`}
                    touched={values.paymentDueDate}
                    error={errors.paymentDueDate}
                    value="-2"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onClick={() => {
                      scheduleSelected(dueDateMinusTwoDay, '-2');
                    }}
                  />
                )}
              </AutopayDueDateWrapper>

              {/* do not display error message, user selection 
                  is only validation required, vetted by NEXT button disabled logic */}

              <AutopayConsentWrapper>
                <AutopayDetailsCheckbox
                  type="checkbox"
                  name="paymentConsent"
                  id="paymentConsent"
                  checked={isChecked}
                  error={errors.paymentConsent}
                  onChange={handleChange}
                  onClick={() => {
                    consentClicked();
                    // validateField('paymentOtherAmountDue');
                  }}
                />
                <div>
                  I acknowledge that I understand automatic payments will begin
                  on the date selected above. I understand that I am responsible
                  for making any payments due prior to this start date. I agree
                  to Bridgecrest’s{' '}
                  <a href="#" onClick={toggleTermsModal}>
                    {' '}
                    Consent to Use Electronic Signatures and Communications.
                  </a>
                </div>
              </AutopayConsentWrapper>
              <AutopayNextButton
                disabled={isSubmitButtonDisabled}
                type="submit"
                id="autopayNextButton"
              >
                NEXT
              </AutopayNextButton>
            </form>
          );
        }}
      />
      {/* Electronics Terms and Conditions Modal */}
      <ElectronicPaymentTerms
        openModal={showTermsModal}
        toggleTermsModal={toggleTermsModal}
      />
    </AutopayDetailsWrapper>
  );
};

export default AutopayDetails;
